/**
 * DraftPreviewModal — "voici ce qui sera publié" (PR2 — 2026-05-10).
 *
 * S'ouvre après un POST /drafts/{id}/approve réussi. Affiche les N
 * composites mockup rendus à l'approve (presigned URLs MinIO TTL 15
 * min) sous forme de grille **réordonnable par drag-and-drop**
 * (2026-05-15). L'ordre courant pilote les rangs Etsy 1..N : le
 * `onConfirmPublish` reçoit l'ordre courant des template_ids, que le
 * caller envoie au backend. `publications_service.enqueue_publish`
 * détecte un cache hit set-based et permute `mockup_ids` selon l'ordre
 * sans re-render (TTL MinIO 15 min absorbe le délai DnD utilisateur).
 *
 * Bouton "Modifier" ferme la modale sans publier — la draft reste
 * approved mais pas en file. L'utilisatrice peut revenir au picker
 * via le bouton "Approuver" sur la DraftCard (qui re-render et écrase
 * le cache si la sélection a changé).
 *
 * Verrous :
 *   - `<Modal>` (pas `window.confirm`).
 *   - `<Button>` pour les actions.
 */

import { useState } from 'react'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Loader2 } from 'lucide-react'

import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import type { DraftMockupPreview } from '@/utils/api/drafts'

import './DraftPreviewModal.css'

interface DraftPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  /** Libellé du draft pour le titre (ex: "Etsy listing"). */
  draftKindLabel: string
  /** Previews retournées par `POST /drafts/{id}/approve`. */
  previews: DraftMockupPreview[]
  /**
   * Confirme la publication avec l'ordre courant des composites.
   * L'array `orderedTemplateIds` est dérivé du DnD interne — caller
   * l'envoie tel quel comme `mockup_template_ids` au backend.
   */
  onConfirmPublish: (orderedTemplateIds: number[]) => void
  isPublishing: boolean
}

export default function DraftPreviewModal({
  isOpen,
  onClose,
  draftKindLabel,
  previews,
  onConfirmPublish,
  isPublishing,
}: DraftPreviewModalProps) {
  // Le caller (DraftsTab) démonte le composant à la fermeture de la
  // modale (`flowState !== 'preview'` → null) — chaque session
  // d'approbation remonte un composant frais, donc useState(previews)
  // est ré-initialisé naturellement. Pas de useEffect de re-sync.
  const [orderedPreviews, setOrderedPreviews] = useState<DraftMockupPreview[]>(previews)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = orderedPreviews.findIndex((p) => p.mockup_id === active.id)
    const newIndex = orderedPreviews.findIndex((p) => p.mockup_id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    setOrderedPreviews(arrayMove(orderedPreviews, oldIndex, newIndex))
  }

  const handleConfirm = () => {
    onConfirmPublish(orderedPreviews.map((p) => p.mockup_template_id))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Voici ce qui sera publié — ${draftKindLabel}`}
      size="large"
    >
      <Modal.Body>
        <p className="draft-preview-modal-hint">
          {orderedPreviews.length} composite{orderedPreviews.length > 1 ? 's' : ''} prêt
          {orderedPreviews.length > 1 ? 's' : ''}. Glisse-dépose pour ajuster l&apos;ordre — le rang
          1 (haut-gauche) devient la première image Etsy. Tu peux fermer cette fenêtre pour modifier
          la sélection.
        </p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={orderedPreviews.map((p) => p.mockup_id)}
            strategy={rectSortingStrategy}
          >
            <ul className="draft-preview-modal-grid">
              {orderedPreviews.map((preview, index) => (
                <SortablePreviewCard
                  key={preview.mockup_id}
                  preview={preview}
                  rank={index + 1}
                  disabled={isPublishing}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="neutral" onClick={onClose} disabled={isPublishing}>
          Modifier
        </Button>
        <Button type="button" variant="primary" onClick={handleConfirm} disabled={isPublishing}>
          {isPublishing ? <Loader2 aria-hidden="true" size={16} /> : null}
          {isPublishing ? 'Mise en file…' : 'Confirmer & publier'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

interface SortablePreviewCardProps {
  preview: DraftMockupPreview
  rank: number
  disabled: boolean
}

function SortablePreviewCard({ preview, rank, disabled }: SortablePreviewCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: preview.mockup_id,
    disabled,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  const className = [
    'draft-preview-modal-card',
    isDragging && 'draft-preview-modal-card--dragging',
    disabled && 'draft-preview-modal-card--disabled',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={className}
      aria-label={`Composite mockup rang ${rank}`}
      {...attributes}
      {...listeners}
    >
      <img
        src={preview.presigned_url}
        alt={`Composite mockup #${preview.mockup_id}`}
        className="draft-preview-modal-card-image"
        loading="lazy"
        draggable={false}
      />
      <div className="draft-preview-modal-card-meta">
        <span className="draft-preview-modal-card-rank">Rang {rank}</span> · {preview.width_px}×
        {preview.height_px}px
      </div>
    </li>
  )
}
